/**
 * One signal, at a stable address.
 *
 * `/signals/K-260825-01` — the permalink a ref exists for. Read-only and
 * deliberately plain: this is where a citation, an exported note or something
 * somebody wrote in a notebook lands, so it has to render the signal itself
 * rather than a view of a list that happens to contain it.
 *
 * **No nav entry.** It is an address you arrive at, not a place you browse to.
 */
import { api, ApiError } from "../lib/api";
import { renderHeader } from "../components/header";
import { renderAssessment, type SignalAssessment } from "../components/assessment";
import type { RouteParams } from "../lib/router";

interface SignalRecord extends SignalAssessment {
  id: string;
  signalRef: string | null;
  title: string;
  source: string;
  sourceUrl: string;
  datePublished: string;
  dateAdded: string;
  summary: string;
  keyImplications: string[];
  wikilinks: string[];
  steeples: Record<string, number>;
  rawExcerpts: string | null;
  /** Which model wrote the summary, implications and scores. null on rows
   *  predating the column and on manually created signals — not "the current
   *  model", which is a different claim and would be false for old rows. */
  analysisModel: string | null;
}

const DIMENSIONS: Array<[string, string]> = [
  ["social", "Social"],
  ["technological", "Technological"],
  ["economic", "Economic"],
  ["environmental", "Environmental"],
  ["political", "Political"],
  ["legal", "Legal"],
  ["ethical", "Ethical"],
  ["security", "Security"],
];

function esc(s: string): string {
  const d = document.createElement("div");
  d.textContent = s;
  return d.innerHTML;
}

export async function renderSignalPage(
  root: HTMLElement,
  params: RouteParams,
): Promise<void> {
  root.appendChild(renderHeader());
  const page = document.createElement("div");
  page.className = "sg-page";
  page.innerHTML = `<p class="muted">Loading…</p>`;
  root.appendChild(page);

  const ref = params["ref"] ?? "";
  let sig: SignalRecord;
  try {
    sig = await api<SignalRecord>(`/signals/${encodeURIComponent(ref)}`);
  } catch (err) {
    // A 404 here is the ordinary case, not a fault: refs get mistyped and
    // exported notes outlive the signals they came from. Say which it is.
    const missing = err instanceof ApiError && err.status === 404;
    page.innerHTML = missing
      ? `<h2>No such signal</h2>
         <p class="muted">Nothing here answers to <code>${esc(ref)}</code>. It may have
         been deleted, or the reference may be mistyped.</p>`
      : `<h2>Could not load that signal</h2>
         <p class="muted">${esc(err instanceof ApiError ? err.message : "Unknown error")}</p>`;
    return;
  }

  const rows = DIMENSIONS.map(
    ([key, label]) => `<tr><td>${label}</td><td>${sig.steeples[key] ?? 0}/10</td></tr>`,
  ).join("");
  const implications = sig.keyImplications.length
    ? `<ul class="sg-implications">${sig.keyImplications.map((i) => `<li>${esc(i)}</li>`).join("")}</ul>`
    : `<p class="muted">None recorded.</p>`;
  const links = sig.wikilinks.length
    ? sig.wikilinks.map((w) => `<span class="ap-role-badge sg-wikilink">${esc(w)}</span>`).join(" ")
    : `<span class="muted">None</span>`;

  // Null when nothing has been assessed, which is every signal collected before
  // v6 and every signal since, until the backfill runs. See renderAssessment.
  const assessment = renderAssessment(sig, sig.datePublished);

  page.innerHTML = `
    <p class="muted sg-ref">${esc(sig.signalRef ?? "no reference")}</p>
    <h2 class="sg-title">${esc(sig.title)}</h2>
    <p class="muted sg-meta">
      <a href="${esc(sig.sourceUrl)}" target="_blank" rel="noopener noreferrer">${esc(sig.source)}</a>
      · published ${esc(sig.datePublished)} · added ${esc(sig.dateAdded)}${
        // Named here rather than hidden in the excerpts fold: with two models
        // serving this install, which one wrote a summary is context for
        // reading it, and the reason the column exists is to make a week on
        // each comparable by eye.
        sig.analysisModel ? ` · analysed by ${esc(sig.analysisModel)}` : ""
      }
    </p>
    <div class="sg-actions">
      <button class="ap-action-btn" id="sg-copy">Copy link</button>
      <button class="ap-action-btn" id="sg-export">Export note</button>
    </div>
    <h3>Summary</h3>
    <p class="sg-summary">${esc(sig.summary)}</p>
    <h3>Key implications</h3>
    ${implications}
    ${assessment ? `<h3>Assessment</h3>${assessment}` : ""}
    <h3>STEEPLES</h3>
    <div class="ap-table-wrap"><table class="ap-table sg-steeples">
      <thead><tr><th>Dimension</th><th>Score</th></tr></thead><tbody>${rows}</tbody>
    </table></div>
    <h3>Wikilinks</h3>
    <p class="sg-wikilinks">${links}</p>
    ${sig.rawExcerpts ? `<details class="sg-excerpts"><summary>Raw excerpts</summary><pre>${esc(sig.rawExcerpts)}</pre></details>` : ""}
  `;

  page.querySelector("#sg-copy")?.addEventListener("click", (ev) => {
    const btn = ev.currentTarget as HTMLButtonElement;
    void navigator.clipboard.writeText(location.href).then(
      () => {
        btn.textContent = "Copied";
        setTimeout(() => { btn.textContent = "Copy link"; }, 1500);
      },
      () => { btn.textContent = "Copy failed"; },
    );
  });

  page.querySelector("#sg-export")?.addEventListener("click", () => {
    // Straight to the endpoint rather than through api(): it answers with a
    // file, and the Content-Disposition filename is the point.
    window.open(`/api/signals/${encodeURIComponent(sig.signalRef ?? sig.id)}/export`, "_blank");
  });
}
